import { CandidatLink } from '../../components/candidat/CandidatLink';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Clock, Check, Loader2, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';
import { VoirResultatsButton } from '../../components/qcm/GrilleResultats';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fil chronologique de l'AF (vue admin) : même rendu synthétique que le portail formateur.
// Clic sur une ligne -> détail de la journée (/dossiers/{id}/journee/{date}).
export const FilChronologiqueTab = ({ dossierId }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);

  const [aftInfo, setAftInfo] = useState({ afts: [], nb_candidats_total: 0, nb_delegues_aft: 0 });
  const [testsOuverts, setTestsOuverts] = useState({});
  useEffect(() => {
    if (!dossierId) return;
    axios.get(`${API_URL}/portail/admin/dossier/${dossierId}/apercu`).then(r => setAftInfo(r.data)).catch(() => {});
  }, [dossierId]);
  useEffect(() => {
    if (!dossierId) return;
    axios.get(`${API_URL}/dossiers/${dossierId}/reports-examens`)
      .then(res => setReports(res.data || []))
      .catch(() => setReports([]));
  }, [dossierId]);

  const fetchFil = useCallback(async () => {
    try {
      const resp = await axios.get(`${API_URL}/portail/formateur/action/${dossierId}`);
      setData(resp.data);
    } catch (err) {
      // erreur silencieuse
    } finally {
      setLoading(false);
    }
  }, [dossierId]);

  useEffect(() => { fetchFil(); }, [fetchFil]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement du fil chronologique...
      </div>
    );
  }
  if (!data) {
    return <p className="text-sm text-slate-400 py-8 text-center">Impossible de charger le fil chronologique.</p>;
  }

  const timeline = data.timeline || [];

  return (
    <Card className="border-0 shadow-sm" data-testid="fil-chronologique-admin">
      <CardContent className="p-5">
        <h2 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
          <Clock className="w-4 h-4 text-indigo-500" /> Fil chronologique ({timeline.length} journée(s))
        </h2>
        {/* Suivi des reports d'examens */}
        {reports.length > 0 && (
          <div className="mb-4 border border-amber-200 bg-amber-50/50 rounded-lg p-3" data-testid="reports-examens-card">
            <h4 className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <GraduationCap className="w-3.5 h-3.5" /> Suivi des passages reportés ({reports.filter(r => r.statut !== 'valide').length} en attente)
            </h4>
            <div className="space-y-1">
              {reports.map((r, i) => (
                <div key={`${r.candidat_id}-${r.module_id}-${r.date_journee || i}`} className="flex items-center justify-between gap-2 bg-white rounded px-2.5 py-1.5 text-xs" data-testid={`report-row-${r.candidat_id}-${r.module_id}`}>
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <CandidatLink dossierId={dossierId} candidatId={r.candidat_id} className="font-medium">{r.candidat_nom}</CandidatLink>
                    <span className="text-[10px] font-mono text-rose-700 bg-rose-50 px-1 py-0.5 rounded">{r.module_code}</span>
                    <span className="text-slate-400">journée du {r.date_journee ? new Date(r.date_journee + 'T00:00:00').toLocaleDateString('fr-FR') : '—'}</span>
                    {r.report_type === 'date' && (
                      <span className="text-[10px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">Reporté au {r.report_date ? new Date(r.report_date + 'T00:00:00').toLocaleDateString('fr-FR') : '?'}</span>
                    )}
                    {r.report_type === 'aft' && (
                      <button
                        type="button"
                        onClick={() => r.aft_id && navigate(`/dossiers/${r.aft_id}`)}
                        className="text-[10px] font-semibold text-cyan-700 bg-cyan-100 hover:bg-cyan-200 px-1.5 py-0.5 rounded transition-colors"
                      >
                        Session {r.aft_code}
                      </button>
                    )}
                    {!r.report_type && <span className="text-[10px] text-slate-400 italic">Sans report planifié</span>}
                  </div>
                  {r.statut === 'valide' ? (
                    <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded shrink-0" title={r.source_aft_code ? `Validé via ${r.source_aft_code}` : ''}>
                      ✓ Validé{r.source_aft_code ? ` (${r.source_aft_code})` : ''}
                    </span>
                  ) : (
                    <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded shrink-0">En attente</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {timeline.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">Aucune journée planifiée. Ajoutez des dates dans l'onglet Planning.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-100 text-slate-500 uppercase tracking-wide">
                  <th className="px-3 py-2 text-left font-medium">Date</th>
                  <th className="px-3 py-2 text-left font-medium">Type</th>
                  <th className="px-3 py-2 text-left font-medium">Formateur</th>
                  <th className="px-3 py-2 text-left font-medium">Lieu</th>
                  <th className="px-3 py-2 text-left font-medium">Module(s)</th>
                  <th className="px-3 py-2 text-center font-medium">Présences</th>
                  <th className="px-3 py-2 text-center font-medium">Évaluations</th>
                  <th className="px-3 py-2 text-center font-medium">Cahier</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {timeline.map(t => {
                  const d = new Date(t.date + 'T00:00:00');
                  const dayLabel = d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
                  const isStage = t.type_journee === 'stage';
                  const isPast = d <= new Date();
                  return (
                    <tr
                      key={t.date}
                      className={`transition-colors ${isStage ? 'bg-violet-50/50' : isPast && !t.has_cahier ? 'bg-amber-50/30' : 'hover:bg-slate-50'} ${!isStage ? 'cursor-pointer' : ''}`}
                      onClick={() => !isStage && navigate(`/dossiers/${dossierId}/journee/${t.date}`, { state: { from: 'fil_chronologique' } })}
                      title={!isStage ? 'Voir le détail de la journée' : undefined}
                      data-testid={`fil-row-${t.date}`}
                    >
                      <td className="px-3 py-2 font-medium text-slate-700 whitespace-nowrap">{dayLabel}</td>
                      <td className="px-3 py-2">
                        {isStage ? (
                          <span className="text-[10px] font-bold text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded">{t.organisme_externe || 'Stage'}</span>
                        ) : (
                          <span className="text-slate-600 capitalize">{t.type_journee}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-600">{t.formateur_nom || <span className="text-slate-300">—</span>}</td>
                      <td className="px-3 py-2 text-slate-600">{isStage ? '—' : (t.lieu || <span className="text-slate-300">—</span>)}</td>
                      <td className="px-3 py-2 text-slate-600 max-w-[300px]">
                        {(t.modules_detail || []).length > 0 ? (
                          <div className="space-y-0.5">
                            {t.modules_detail.map((m) => {
                              const isCert = (m.code || '').toUpperCase().startsWith('CERT');
                              return (
                                <div key={m.module_id} className="flex items-center gap-1.5 text-xs">
                                  <span className="truncate" title={m.titre}>{m.titre || m.code}</span>
                                  {isCert && (
                                    <>
                                      <span className="text-[9px] font-bold text-rose-700 bg-rose-100 px-1 py-0.5 rounded shrink-0">CERT</span>
                                      {(() => {
                                        const aft = (aftInfo.afts || []).find(a => a.module_id === m.module_id);
                                        if (aft) {
                                          const restants = aft.nb_restants != null ? aft.nb_restants : Math.max(0, (aftInfo.nb_candidats_total || 0) - (aftInfo.nb_delegues_aft || 0));
                                          return (
                                            <button type="button" onClick={(e) => { e.stopPropagation(); navigate(`/dossiers/${aft.id}`); }}
                                              className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-cyan-600 text-white hover:bg-cyan-700 transition-colors"
                                              title={`${aft.code} — ${aft.nb_candidats} candidat(s) délégué(s)${aft.nb_passes_af != null ? `, ${aft.nb_passes_af} passé(s) sur l'AF mère` : ''}, ${restants} reste(nt) à passer`}
                                              data-testid={`btn-voir-aft-${t.date}-${m.module_id}`}>
                                              <GraduationCap className="w-2.5 h-2.5" /> Voir l'AFT · {aft.nb_candidats} délégué(s) · {restants} reste(nt) à passer
                                            </button>
                                          );
                                        }
                                        return (
                                          <button type="button" onClick={async (e) => { e.stopPropagation(); try { const r = await axios.post(`${API_URL}/dossiers/${dossierId}/sync-afts`); toast.success(r.data.creees.length ? `AFT ${r.data.creees.map(a => a.code).join(', ')} créée(s)` : 'AFT synchronisées'); const ap = await axios.get(`${API_URL}/portail/admin/dossier/${dossierId}/apercu`); setAftInfo(ap.data); } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); } }}
                                            className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-cyan-100 text-cyan-700 hover:bg-cyan-200 border border-cyan-200 transition-colors" title="Créer l'AFT manquante pour ce module certifiant" data-testid={`btn-creer-aft-manquante-${t.date}-${m.module_id}`}>
                                            <GraduationCap className="w-2.5 h-2.5" /> Créer l'AFT manquante
                                          </button>
                                        );
                                      })()}
                                    </>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (t.modules || []).length > 0 ? (
                          <div className="space-y-0.5">
                            {t.modules.map((m) => (
                              <div key={m} className="text-xs truncate" title={m}>{m}</div>
                            ))}
                          </div>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {isStage ? '—' : t.presences_validees ? (
                          <span className="text-emerald-600 font-semibold flex items-center justify-center gap-1"><Check className="w-3 h-3" /> Oui</span>
                        ) : isPast ? (
                          <span className="text-amber-500 font-medium">Non</span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center align-top">
                        {(t.tests || []).length > 0 ? (
                          <div className="flex flex-col items-center gap-0.5" data-testid={`fil-tests-${t.date}`}>
                            <button type="button" onClick={(e) => { e.stopPropagation(); setTestsOuverts(o => ({ ...o, [t.date]: !o[t.date] })); }} className="flex flex-col items-center gap-0.5 hover:bg-indigo-50 rounded px-1.5 py-0.5" title="Voir le détail des tests de la journée" data-testid={`fil-tests-toggle-${t.date}`}>
                              {t.eval_detail && (
                                <span className={`text-[11px] font-bold ${t.eval_detail.candidats_evalues >= t.eval_detail.total_candidats ? 'text-emerald-600' : 'text-indigo-600'}`}>
                                  QCM {t.eval_detail.candidats_evalues}/{t.eval_detail.total_candidats} passé{t.eval_detail.moyenne != null ? ` · ${t.eval_detail.moyenne}%` : ''}
                                </span>
                              )}
                              {t.eval_manuelles_detail && (
                                <span className="text-[11px] font-bold text-violet-700" data-testid={`fil-evals-${t.date}`}>
                                  Éval. {t.eval_manuelles_detail.candidats_evalues}/{t.eval_detail?.total_candidats ?? data?.candidats?.length ?? '?'} évalué{t.eval_manuelles_detail.moyenne != null ? ` · ${t.eval_manuelles_detail.moyenne}%` : ''}
                                </span>
                              )}
                              <span className="text-[9px] text-slate-400">{testsOuverts[t.date] ? '▲ replier' : `▼ ${t.tests.length} test${t.tests.length > 1 ? 's' : ''}`}</span>
                            </button>
                            {testsOuverts[t.date] && (
                              <div className="w-full text-left mt-1 space-y-1 border-t border-slate-100 pt-1" onClick={(e) => e.stopPropagation()} data-testid={`fil-tests-detail-${t.date}`}>
                                {t.tests.map(ts => (
                                  <div key={`${ts.type}-${ts.id}`} className="flex items-center gap-1.5 text-[10px]">
                                    <span className={`shrink-0 px-1 rounded font-bold ${ts.type === 'qcm' ? 'bg-indigo-100 text-indigo-700' : 'bg-violet-100 text-violet-700'}`}>{ts.type === 'qcm' ? 'QCM' : 'ÉVAL'}</span>
                                    <span className="flex-1 min-w-0 truncate text-slate-700" title={ts.titre}>{ts.titre}</span>
                                    <span className="shrink-0 text-slate-500">{ts.nb_candidats} cand.{ts.moyenne != null ? ` · ${ts.moyenne}%` : ''}</span>
                                    <VoirResultatsButton testType={ts.type} testId={ts.id} dossierId={dossierId} dateJournee={t.date} titre={ts.titre} />
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : t.evaluations_count > 0 ? (
                          <span className="text-indigo-600 font-semibold">{t.evaluations_count}</span>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {t.has_cahier ? (
                          <span className="text-emerald-600"><Check className="w-3.5 h-3.5 mx-auto" /></span>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {/* Confirmation création AFT */}
      </CardContent>
    </Card>
  );
};
